import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FiLock, FiCheckCircle, FiClock, FiUnlock, FiEye, FiEyeOff, FiMap } from 'react-icons/fi';
import { listTrainingModules, startLevelExam } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { CAREER_TRACKS } from '../components/CareerBadge';

const TRACK_ORDER = [
  'Field Safety',
  'Computer Literacy',
  'Survey Technician',
  'Instrument Operator',
  'Office Technician',
  'CAD Technician',
  'Registered Land Surveyor',
  'Licensure Prep'
];

const LICENSURE_PREP = {
  color: '#dc2626',
  bg: '#fee2e2'
};

function trackStyle(track) {
  if (track === 'Licensure Prep') return LICENSURE_PREP;
  return CAREER_TRACKS[track] || { color: '#475569', bg: '#f1f5f9' };
}

function levelLabel(track, level) {
  if (track === 'Licensure Prep') {
    return ['', 'FS Exam Prep', 'PS Exam Prep', 'Georgia State Prep'][level] || `Level ${level}`;
  }
  return `Level ${level}`;
}

export default function Training() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [unlockCode, setUnlockCode] = useState('');
  const [unlockBusy, setUnlockBusy] = useState(false);
  const [unlockError, setUnlockError] = useState('');
  const [showAllModules, setShowAllModules] = useState(() => {
    return localStorage.getItem('training_show_all_modules') === 'true';
  });
  const isAdmin = !!user?.can_manage_payroll;

  // Persist toggle preference
  useEffect(() => {
    localStorage.setItem('training_show_all_modules', String(showAllModules));
  }, [showAllModules]);

  useEffect(() => {
    let mounted = true;
    listTrainingModules()
      .then(res => {
        if (!mounted) return;
        setModules(res.data.modules || []);
      })
      .catch(err => {
        if (!mounted) return;
        setError(err.response?.data?.error || err.message || 'Failed to load training modules.');
      })
      .finally(() => mounted && setLoading(false));
    return () => { mounted = false; };
  }, []);

  const handleEnterCode = async () => {
    setUnlockBusy(true);
    setUnlockError('');
    try {
      const res = await startLevelExam(unlockCode.trim());
      navigate(`/training/exam/${res.data.attempt_id}`);
    } catch (err) {
      setUnlockError(err.response?.data?.error || 'Could not start exam.');
    } finally {
      setUnlockBusy(false);
    }
  };

  // Apply focused-view filter unless user has toggled to show all.
  // Server provides visible_by_default; treat missing flag as visible (backward compat).
  const visibleModules = showAllModules
    ? modules
    : modules.filter(m => m.visible_by_default !== false);
  const hiddenCount = modules.length - visibleModules.length;

  // Group modules by track
  const grouped = TRACK_ORDER.map(track => ({
    track,
    modules: visibleModules
      .filter(m => m.track === track)
      .sort((a, b) => a.level - b.level)
  })).filter(g => g.modules.length > 0);

  return (
    <div style={{ padding: '24px 28px', maxWidth: 1200, margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
        <div>
          <h1 style={{ margin: 0, color: '#0f172a' }}>Training & Certification</h1>
          <p style={{ margin: '6px 0 0 0', color: '#64748b', fontSize: 14 }}>
            NSPS CST-aligned study modules + Georgia-specific licensure prep. Practice quizzes are open;
            level exams are proctored — request from your module page when you're ready.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          <Link
            to="/my-career-path"
            style={{
              display: 'inline-flex',
              gap: 8,
              alignItems: 'center',
              background: '#0891b2',
              color: 'white',
              padding: '10px 16px',
              borderRadius: 8,
              textDecoration: 'none',
              fontSize: 14,
              fontWeight: 600
            }}
          >
            <FiMap /> My Career Path
          </Link>
          {isAdmin && (
            <Link
              to="/training/admin"
              style={{
                display: 'inline-flex',
                gap: 8,
                alignItems: 'center',
                background: '#1e293b',
                color: 'white',
                padding: '10px 16px',
                borderRadius: 8,
                textDecoration: 'none',
                fontSize: 14,
                fontWeight: 600
              }}
            >
              <FiUnlock /> Admin / Proctor
            </Link>
          )}
        </div>
      </div>

      {/* Quick: enter unlock code */}
      <div
        style={{
          background: '#fff7ed',
          border: '1px solid #fed7aa',
          borderRadius: 10,
          padding: '12px 16px',
          marginBottom: 24,
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          flexWrap: 'wrap'
        }}
      >
        <FiLock color="#c2410c" />
        <span style={{ fontSize: 14, color: '#9a3412' }}>
          Have an unlock code from your proctor? Enter it to start your exam:
        </span>
        <input
          type="text"
          inputMode="numeric"
          maxLength={6}
          value={unlockCode}
          onChange={e => setUnlockCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
          placeholder="6-digit code"
          style={{
            padding: '8px 12px',
            border: '1px solid #fdba74',
            borderRadius: 6,
            fontSize: 16,
            letterSpacing: 4,
            fontFamily: 'Consolas, Menlo, monospace',
            width: 130,
            textAlign: 'center'
          }}
        />
        <button
          disabled={unlockBusy || unlockCode.length !== 6}
          onClick={handleEnterCode}
          style={{
            background: '#c2410c',
            color: 'white',
            border: 'none',
            padding: '8px 16px',
            borderRadius: 6,
            cursor: unlockCode.length === 6 ? 'pointer' : 'not-allowed',
            opacity: unlockCode.length === 6 ? 1 : 0.5,
            fontSize: 14,
            fontWeight: 600
          }}
        >
          {unlockBusy ? 'Starting…' : 'Start exam'}
        </button>
        {unlockError && (
          <span style={{ color: '#b91c1c', fontSize: 13, width: '100%' }}>{unlockError}</span>
        )}
      </div>

      {/* View toggle: focused (current path) vs all modules */}
      {!loading && !error && modules.length > 0 && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
          padding: '10px 14px',
          marginBottom: 16,
          background: '#f8fafc',
          border: '1px solid #e2e8f0',
          borderRadius: 8,
          flexWrap: 'wrap'
        }}>
          <div style={{ fontSize: 13, color: '#475569' }}>
            {showAllModules ? (
              <>Showing all {modules.length} modules.</>
            ) : (
              <>
                Showing {visibleModules.length} module{visibleModules.length === 1 ? '' : 's'} on your current path
                {hiddenCount > 0 && (
                  <span style={{ color: '#94a3b8' }}> ({hiddenCount} more available)</span>
                )}
                .
              </>
            )}
          </div>
          <button
            type="button"
            onClick={() => setShowAllModules(!showAllModules)}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '6px 12px',
              background: showAllModules ? '#1e293b' : 'white',
              color: showAllModules ? 'white' : '#1e293b',
              border: '1px solid #1e293b',
              borderRadius: 6,
              cursor: 'pointer',
              fontSize: 13,
              fontWeight: 600
            }}
          >
            {showAllModules ? <><FiEyeOff /> Show only my path</> : <><FiEye /> Show all modules</>}
          </button>
        </div>
      )}

      {loading && <p>Loading…</p>}
      {error && (
        <div style={{ background: '#fee2e2', color: '#991b1b', padding: 12, borderRadius: 8 }}>
          {error}
        </div>
      )}

      {!loading && !error && grouped.length === 0 && (
        <div style={{ textAlign: 'center', padding: 60, color: '#64748b' }}>
          {modules.length === 0
            ? 'No training modules have been published yet.'
            : 'No modules on your current path. Toggle "Show all modules" to browse the full curriculum.'}
        </div>
      )}

      {grouped.map(group => {
        const style = trackStyle(group.track);
        return (
          <section key={group.track} style={{ marginBottom: 32 }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                marginBottom: 12
              }}
            >
              <div
                style={{
                  width: 8,
                  height: 28,
                  borderRadius: 4,
                  background: style.color
                }}
              />
              <h2 style={{ margin: 0, color: '#0f172a' }}>{group.track}</h2>
            </div>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
                gap: 14
              }}
            >
              {group.modules.map(m => (
                <ModuleCard key={m.id} module={m} style={style} />
              ))}
            </div>
          </section>
        );
      })}
    </div>
  );
}

function ModuleCard({ module, style }) {
  const passed = module.has_passed;
  const pct = module.progress_pct || 0;

  return (
    <Link
      to={`/training/modules/${module.id}`}
      style={{
        textDecoration: 'none',
        color: 'inherit',
        background: 'white',
        border: `1px solid ${style.color}33`,
        borderRadius: 10,
        padding: 16,
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
        boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
        transition: 'transform 0.1s, box-shadow 0.1s',
        position: 'relative'
      }}
      onMouseEnter={e => {
        e.currentTarget.style.transform = 'translateY(-2px)';
        e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.08)';
      }}
      onMouseLeave={e => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = '0 1px 2px rgba(0,0,0,0.04)';
      }}
    >
      {passed && (
        <div
          style={{
            position: 'absolute',
            top: 12,
            right: 12,
            display: 'flex',
            alignItems: 'center',
            gap: 4,
            color: '#16a34a',
            fontSize: 12,
            fontWeight: 700
          }}
        >
          <FiCheckCircle size={14} /> Passed
        </div>
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span
          style={{
            background: style.bg,
            color: style.color,
            fontSize: 11,
            padding: '3px 10px',
            borderRadius: 12,
            fontWeight: 700
          }}
        >
          {levelLabel(module.track, module.level)}
        </span>
        <span style={{ fontSize: 12, color: '#64748b' }}>
          {module.lesson_count} lesson{module.lesson_count === 1 ? '' : 's'}
        </span>
      </div>
      <h3 style={{ margin: 0, fontSize: 16, color: '#0f172a' }}>{module.title}</h3>
      <p style={{ margin: 0, fontSize: 13, color: '#475569', lineHeight: 1.45, flex: 1 }}>
        {module.summary}
      </p>
      {/* Progress bar */}
      <div style={{ marginTop: 4 }}>
        <div
          style={{
            height: 6,
            background: '#e2e8f0',
            borderRadius: 3,
            overflow: 'hidden'
          }}
        >
          <div
            style={{
              width: `${pct}%`,
              height: '100%',
              background: passed ? '#16a34a' : style.color,
              transition: 'width 0.3s'
            }}
          />
        </div>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            marginTop: 4,
            fontSize: 11,
            color: '#64748b'
          }}
        >
          <span>{module.lessons_completed}/{module.lesson_count} lessons</span>
          <span>{pct}%</span>
        </div>
      </div>
      {module.pending_unlock && (
        <div
          style={{
            background: '#dbeafe',
            color: '#1e40af',
            fontSize: 12,
            padding: '6px 8px',
            borderRadius: 6,
            display: 'flex',
            alignItems: 'center',
            gap: 6
          }}
        >
          <FiClock size={12} />
          Exam {module.pending_unlock.status === 'unlocked' ? 'unlocked — ask proctor for code' : 'request pending admin approval'}
        </div>
      )}
    </Link>
  );
}
